import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './styles/admin.scss';
import { ApiHelper } from '../../apiHelper/ApiHelper';
import ConfirmModal from '../../components/ConfirmModal';
import MarkdownEditor from '../../components/admin/MarkdownEditor';
import { resolveBlogImage } from '../../Constants';

const MAX_IMAGE_MB = 10;

type Post = { id?: string; heading: string; image: string; text: string; url: string; author: string; keyWords: string; description: string; published: boolean };
const blank: Post = { heading: "", image: "", text: "", url: "", author: "", keyWords: "", description: "", published: false };

export default function AdminBlogEditor() {
    const { id } = useParams();
    const navigate = useNavigate();
    const api = new ApiHelper();
    const isNew = id === "new";

    const [p, setP] = useState<Post | null>(null);
    const [confirmDel, setConfirmDel] = useState(false);
    const [uploadingImg, setUploadingImg] = useState(false);

    async function uploadImage(file: File) {
        if (!file.type.startsWith("image/")) { api.alert.error("Vyber obrázek"); return; }
        if (file.size > MAX_IMAGE_MB * 1024 * 1024) { api.alert.error(`Obrázek je příliš velký (max ${MAX_IMAGE_MB} MB)`); return; }
        setUploadingImg(true);
        try {
            const res = await (await api.adminContentApi.upload(file)).json();
            setP(prev => prev ? { ...prev, image: res.url } : prev);
            api.alert.success("Obrázek nahrán");
        } catch (x: any) { api.adminError(x); }
        finally { setUploadingImg(false); }
    }

    useEffect(() => { load(); }, [id]);
    async function load() {
        if (isNew) { setP({ ...blank }); return; }
        try { setP(await (await api.adminBlogApi.detail(id!)).json()); } catch (x: any) { api.adminError(x); }
    }

    async function save() {
        if (!p) return;
        if (!p.heading.trim()) { api.alert.error("Zadej nadpis"); return; }
        const payload = { heading: p.heading, image: p.image, text: p.text, url: p.url, author: p.author, keyWords: p.keyWords, description: p.description, published: p.published };
        try {
            if (isNew) { const r = await (await api.adminBlogApi.create(payload)).json(); api.alert.success("Článek vytvořen"); navigate("/admin/blog/" + r.id, { replace: true }); }
            else { await api.adminBlogApi.update(id!, payload); api.alert.success("Uloženo"); await load(); }
        } catch (x: any) { api.adminError(x); }
    }
    async function del() { try { await api.adminBlogApi.remove(id!); api.alert.success("Smazáno"); navigate("/admin/blog"); } catch (x: any) { api.adminError(x); } }

    if (!p) return <div><span className="backLink" onClick={() => navigate("/admin/blog")}>← Blog</span></div>;

    return (
        <div>
            <span className="backLink" onClick={() => navigate("/admin/blog")}>← Blog</span>
            <div className="adminHead">
                <div><h1>{isNew ? "Nový článek" : p.heading || "Článek"}</h1></div>
                <div className="aActions">
                    {!isNew && <button className="aBtn danger" onClick={() => setConfirmDel(true)}>Smazat</button>}
                    <button className="aBtn" onClick={save}>{isNew ? "Vytvořit" : "Uložit"}</button>
                </div>
            </div>

            <div className="aStack">
                <div className="aCard">
                    <div className="aField"><label>Nadpis</label><input className="bigInput" value={p.heading} onChange={e => setP({ ...p, heading: e.target.value })} placeholder="Nadpis článku" /></div>
                    <div className="aField"><label>Text</label><MarkdownEditor value={p.text} onChange={t => setP({ ...p, text: t })} fill placeholder="Napiš článek…" /></div>
                </div>
                <div className="aCard">
                    <h2>Metadata</h2>
                    <div className="aField"><label>Stav</label>
                        <label className="toggle"><input type="checkbox" checked={p.published} onChange={e => setP({ ...p, published: e.target.checked })} /><i className="slider" /> {p.published ? "Publikováno (na webu)" : "Koncept (skryté)"}</label>
                    </div>
                    <div className="inlineRow">
                        <div className="aField inline"><label>URL (slug)</label><input value={p.url} onChange={e => setP({ ...p, url: e.target.value })} placeholder="nazev-clanku" /></div>
                        <div className="aField inline"><label>Autor</label><input value={p.author} onChange={e => setP({ ...p, author: e.target.value })} /></div>
                    </div>
                    <div className="aField">
                        <label>Titulní obrázek</label>
                        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 8 }}>
                            {p.image
                                ? <img src={resolveBlogImage(p.image)} alt="" style={{ width: 180, height: 100, objectFit: "cover", borderRadius: 8, border: "1px solid rgba(51,101,138,0.25)", background: "#f2f6f7" }} />
                                : <div style={{ width: 180, height: 100, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 8, border: "1px dashed rgba(51,101,138,0.35)", background: "#f2f6f7", fontSize: 13, opacity: 0.7 }}>Bez obrázku</div>}
                            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                <label className="aBtn" style={{ cursor: "pointer", opacity: uploadingImg ? 0.6 : 1 }}>
                                    {uploadingImg ? "Nahrávám…" : "Nahrát obrázek"}
                                    <input type="file" accept="image/*" hidden disabled={uploadingImg}
                                        onChange={e => { const f = e.target.files?.[0]; if (f) uploadImage(f); e.currentTarget.value = ""; }} />
                                </label>
                                {p.image && <button className="aBtn ghost" type="button" onClick={() => setP({ ...p, image: "" })}>Odebrat</button>}
                            </div>
                        </div>
                        <input value={p.image} onChange={e => setP({ ...p, image: e.target.value })} placeholder="/uploads/… nebo https://… (vyplní se po nahrání)" />
                    </div>
                    <div className="aField"><label>Klíčová slova</label><input value={p.keyWords} onChange={e => setP({ ...p, keyWords: e.target.value })} /></div>
                    <div className="aField"><label>Popis (SEO)</label><textarea value={p.description} onChange={e => setP({ ...p, description: e.target.value })} /></div>
                </div>
            </div>

            <ConfirmModal open={confirmDel} title="Smazat článek?" message="Článek bude trvale smazán." confirmLabel="Smazat" cancelLabel="Zrušit" onConfirm={() => { setConfirmDel(false); del(); }} onClose={() => setConfirmDel(false)} />
        </div>
    );
}
